import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api } from '../lib/api.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useSortable } from '../hooks/useSortable.js';
import { SortableTh } from '../components/SortableTh.js';
import { StatCard } from '../components/StatCard.js';
import { AddEhrForm } from '../components/AddEhrForm.js';
import { PageHeader } from '../components/PageHeader.js';
import { EmptyState } from '../components/EmptyState.js';
import type { EhrRow } from '../lib/api.js';

export function EhrsPage() {
  const navigate = useNavigate();
  usePageTitle('EHR Registry');
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const { sortKey, sortDir, toggle, sort } = useSortable<'displayName' | 'authStyle' | 'sessions' | 'totalRecords' | 'openConflicts'>('displayName');

  const { data, isLoading } = useQuery({
    queryKey: ['ehrs'],
    queryFn: () => api.listEhrs(),
  });

  const ehrs: EhrRow[] = data?.ehrs ?? [];

  const searchFiltered = search.trim()
    ? ehrs.filter(
        (e) =>
          e.displayName.toLowerCase().includes(search.toLowerCase()) ||
          e.slug.toLowerCase().includes(search.toLowerCase()),
      )
    : ehrs;
  const filtered = sort(searchFiltered, (e) => {
    if (sortKey === 'sessions') return e.stats?.sessions ?? 0;
    if (sortKey === 'totalRecords') return e.stats?.totalRecords ?? 0;
    if (sortKey === 'openConflicts') return e.stats?.openConflicts ?? 0;
    return (e[sortKey as keyof EhrRow] as string) ?? '';
  });

  function handleEhrCreated(ehrId: string) {
    setShowAdd(false);
    void qc.invalidateQueries({ queryKey: ['ehrs'] });
    void navigate({ to: '/ehrs/$id', params: { id: ehrId } });
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="EHR Registry"
        title="Known EHRs"
        actions={
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition-opacity"
            style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)', boxShadow: '0 2px 12px rgba(13,207,184,0.3)' }}
          >
            + Add EHR
          </button>
        }
      />

      <section className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Stats row */}
        <div className="grid grid-cols-4 gap-4">
          <StatCard label="EHRs" value={String(ehrs.length)} icon="⬢" colorIndex={0} />
          <StatCard
            label="With recipe"
            value={String(ehrs.filter((e) => e.recipePath).length)}
            icon="✓"
            colorIndex={1}
          />
          <StatCard
            label="Total records"
            value={String(ehrs.reduce((n, e) => n + (e.stats?.totalRecords ?? 0), 0))}
            icon="◈"
            colorIndex={3}
          />
          <StatCard
            label="Open conflicts"
            value={String(ehrs.reduce((n, e) => n + (e.stats?.openConflicts ?? 0), 0))}
            icon="!"
            colorIndex={2}
          />
        </div>

        {/* Add EHR form */}
        {showAdd && (
          <div
            className="glass rounded-2xl p-6"
            style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}
          >
            <div className="mb-4 text-sm font-semibold text-text-primary">Add EHR</div>
            <AddEhrForm onCreated={handleEhrCreated} onCancel={() => setShowAdd(false)} />
          </div>
        )}

        {/* EHR table */}
        <div
          className="glass rounded-2xl overflow-hidden"
          style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-border gap-4">
            <div className="text-sm font-semibold text-text-primary shrink-0">All EHRs</div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search EHRs…"
              className="rounded-xl px-3 py-1.5 text-sm outline-none w-64"
              style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
            />
            <div className="text-xs text-text-dimmed shrink-0">
              {filtered.length}{search ? ` of ${ehrs.length}` : ''} registered
            </div>
          </div>

          {isLoading ? (
            <div className="p-8 text-center text-sm text-text-dimmed">Loading…</div>
          ) : filtered.length === 0 ? (
            ehrs.length === 0 ? (
              <EmptyState
                icon="⬢"
                title="No EHRs registered yet"
                description="Add your first EHR to begin the discovery and import process."
                action={{ label: '+ Add EHR', onClick: () => setShowAdd(true) }}
              />
            ) : (
              <EmptyState icon="🔍" title={`No EHRs match "${search}"`} description="Try a different search term." />
            )
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <SortableTh label="EHR" sortKey="displayName" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} />
                  <SortableTh label="Auth" sortKey="authStyle" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} />
                  <Th>Recipe</Th>
                  <SortableTh label="Sessions" sortKey="sessions" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} right />
                  <SortableTh label="Records" sortKey="totalRecords" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} right />
                  <SortableTh label="Conflicts" sortKey="openConflicts" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} right />
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr
                    key={e.id}
                    onClick={() => void navigate({ to: '/ehrs/$id', params: { id: e.id } })}
                    className="border-b border-border last:border-0 transition-colors duration-100 cursor-pointer hover:bg-bg-tertiary"
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white"
                          style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
                        >
                          {e.displayName[0]?.toUpperCase()}
                        </div>
                        <div>
                          <div className="font-semibold text-text-primary">{e.displayName}</div>
                          <div className="font-mono text-xs text-text-dimmed">{e.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className="rounded-md border px-2 py-0.5 font-mono text-xs"
                        style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
                        {e.authStyle}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs">
                      {e.recipePath ? (
                        <span style={{ color: 'var(--accent-green)' }}>✓ {e.recipePath.split('/').pop()}</span>
                      ) : (
                        <span className="text-text-dimmed">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums text-text-secondary">{e.stats?.sessions ?? 0}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-text-secondary">{e.stats?.totalRecords ?? 0}</td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      {(e.stats?.openConflicts ?? 0) > 0 ? (
                        <span style={{ color: 'var(--accent-red)' }}>{e.stats.openConflicts}</span>
                      ) : (
                        <span className="text-text-dimmed">0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th className={`px-5 py-3 text-xs font-semibold uppercase tracking-wider text-text-secondary ${right ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  );
}
